/* ==========================================================================
   Scroll animations, effects & UI interactions
   ========================================================================== */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const onReady = (fn) => {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  /* ---------- Split text: wrap each character in a span ---------- */
  const splitLetters = (el) => {
    if (el.dataset.split === 'true') return;
    el.dataset.split = 'true';
    const words = el.textContent.trim().split(/\s+/);
    el.textContent = '';
    let i = 0;
    words.forEach((word, wIdx) => {
      const wordEl = document.createElement('span');
      wordEl.className = 'split-word';
      [...word].forEach((char) => {
        const span = document.createElement('span');
        span.className = 'split-letter';
        span.style.setProperty('--i', i++);
        span.textContent = char;
        wordEl.appendChild(span);
      });
      el.appendChild(wordEl);
      if (wIdx < words.length - 1) el.appendChild(document.createTextNode(' '));
    });
  };

  const splitWords = (el) => {
    if (el.dataset.split === 'true') return;
    el.dataset.split = 'true';
    const text = el.textContent.trim();
    el.textContent = '';
    text.split(/(\s+)/).forEach((chunk) => {
      if (/^\s+$/.test(chunk)) {
        el.appendChild(document.createTextNode(' '));
      } else if (chunk) {
        const w = document.createElement('span');
        w.className = 'reveal-word';
        w.textContent = chunk;
        el.appendChild(w);
      }
    });
  };

  onReady(() => {
    document.querySelectorAll('[data-animate="letters"]').forEach(splitLetters);
    document.querySelectorAll('[data-animate="scroll-words"]').forEach(splitWords);
  });

  /* ---------- Intersection reveal ---------- */
  onReady(() => {
    const targets = document.querySelectorAll('[data-animate]');
    if (!targets.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      targets.forEach((t) => t.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            if (entry.target.dataset.animateOnce !== 'false' && entry.target.dataset.animate !== 'scroll-words') {
              io.unobserve(entry.target);
            }
          } else if (entry.target.dataset.animateOnce === 'false') {
            entry.target.classList.remove('is-visible');
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
    );

    targets.forEach((t) => io.observe(t));
  });

  /* ---------- Scroll-linked word reveal ---------- */
  onReady(() => {
    const containers = document.querySelectorAll('[data-animate="scroll-words"]');
    if (!containers.length || reduced) return;

    let ticking = false;
    const update = () => {
      const vh = window.innerHeight;
      containers.forEach((container) => {
        const words = container.querySelectorAll('.reveal-word');
        if (!words.length) return;
        const rect = container.getBoundingClientRect();
        // Progress based on how far the top of the container has scrolled past 70% viewport height
        const start = vh * 0.85;
        const end = vh * 0.25;
        let progress = (start - rect.top) / (start - end);
        progress = Math.min(1, Math.max(0, progress));
        const reveal = Math.floor(progress * words.length);
        words.forEach((w, i) => {
          w.classList.toggle('is-revealed', i < reveal);
        });
      });
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  });

  /* ---------- Parallax ---------- */
  onReady(() => {
    const items = document.querySelectorAll('[data-parallax]');
    if (!items.length || reduced) return;
    let ticking = false;
    const update = () => {
      const vh = window.innerHeight;
      items.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;
        const speed = Number(el.dataset.parallax) || 0.25;
        const offset = (rect.top - vh / 2) * -speed;
        el.style.setProperty('--parallax-y', `${offset.toFixed(1)}px`);
      });
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  });

  /* ---------- Scroll to top ---------- */
  onReady(() => {
    const btn = document.querySelector('[data-scroll-top]');
    if (!btn) return;
    const toggle = () => btn.classList.toggle('is-visible', window.scrollY > 400);
    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    });
    window.addEventListener('scroll', toggle, { passive: true });
    toggle();
  });

  /* ---------- Header scrolled state ---------- */
  onReady(() => {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    const toggle = () => header.classList.toggle('is-scrolled', window.scrollY > 20);
    window.addEventListener('scroll', toggle, { passive: true });
    toggle();
  });

  /* ---------- Dark/Light toggle ---------- */
  onReady(() => {
    const btn = document.querySelector('[data-theme-toggle]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      const root = document.documentElement;
      const current = root.getAttribute('data-color-scheme');
      const next = current === 'dark' ? 'light' : 'dark';
      root.setAttribute('data-color-scheme', next);
      try { localStorage.setItem('color-scheme', next); } catch (e) {}
    });
  });

  /* ---------- Mega-menu dropdown (click-toggle for touch, hover via CSS) ---------- */
  onReady(() => {
    document.querySelectorAll('.has-dropdown > a').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        const parent = trigger.parentElement;
        if (!parent.querySelector('.dropdown')) return;
        e.preventDefault();
        const open = parent.dataset.open === 'true';
        document.querySelectorAll('.has-dropdown').forEach((el) => el.dataset.open = 'false');
        parent.dataset.open = String(!open);
      });
    });
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.has-dropdown')) {
        document.querySelectorAll('.has-dropdown').forEach((el) => el.dataset.open = 'false');
      }
    });
  });

  /* ---------- Testimonials slider ---------- */
  onReady(() => {
    document.querySelectorAll('[data-testimonial-slider]').forEach((slider) => {
      const track = slider.querySelector('[data-testimonial-track]');
      const slides = track ? track.querySelectorAll('.testimonials__slide') : [];
      const dotsWrap = slider.querySelector('[data-testimonial-dots]');
      const prev = slider.querySelector('[data-testimonial-prev]');
      const next = slider.querySelector('[data-testimonial-next]');
      if (!track || slides.length === 0) return;

      // Build dots
      if (dotsWrap) {
        slides.forEach((_, i) => {
          const dot = document.createElement('button');
          dot.className = 'testimonials__dot';
          dot.setAttribute('aria-current', i === 0 ? 'true' : 'false');
          dot.addEventListener('click', () => goTo(i));
          dotsWrap.appendChild(dot);
        });
      }
      const dots = dotsWrap ? dotsWrap.querySelectorAll('.testimonials__dot') : [];

      let idx = 0;
      const goTo = (i) => {
        idx = Math.max(0, Math.min(slides.length - 1, i));
        slides[idx].scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
        dots.forEach((d, di) => d.setAttribute('aria-current', di === idx ? 'true' : 'false'));
      };
      prev?.addEventListener('click', () => goTo(idx - 1));
      next?.addEventListener('click', () => goTo(idx + 1));

      // Update active on scroll
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const i = [...slides].indexOf(entry.target);
            if (i !== -1) {
              idx = i;
              dots.forEach((d, di) => d.setAttribute('aria-current', di === idx ? 'true' : 'false'));
            }
          }
        });
      }, { root: track, threshold: 0.6 });
      slides.forEach((s) => io.observe(s));
    });
  });
})();
